/**
 * components/ExpenseFormModal.js
 * Add or edit ONE standalone Pengeluaran — a cost of ours that isn't tied to any transaction,
 * e.g. parking on a quiet day (Biaya Lain-Lain, 2026-10-11; see utils/expenses.js). Opened only
 * from the Pengeluaran page. A cost LINKED to a transaction is never edited here — its one
 * editing surface is that transaction's own form (Edit Transaksi, the "Biaya Lain-Lain" box).
 * A cost whose transaction was deleted since is treated as standalone, so it lands here too;
 * `orphanNote` says so.
 *
 * Props: { initial (an expense, or null to add), orphanNote, branches,
 *          onSave(fields) -> {ok, error}, onDelete() (edit only), onClose }
 *
 * Nothing is pre-chosen except the date (today) — location and category start empty and must be
 * picked. A rejected save keeps the form open with the reason.
 */
import React, { useState } from "react";
import { useModalEsc, backdropClose } from "../utils/modalStack";
import { today } from "../utils/idGenerators";
import { formatCurrencyInput, parseCurrencyInput } from "../utils/currency";
import { EXPENSE_CATEGORIES } from "../utils/expenses";
import DeleteConfirmModal from "./DeleteConfirmModal";

export default function ExpenseFormModal({ initial = null, orphanNote = "", branches = [], onSave, onDelete, onClose }) {
  useModalEsc(onClose);
  const isEdit = !!initial;
  const start = initial || { date: today(), location: null, branchId: null, category: "", paidTo: "", amount: null, note: "" };
  const [date, setDate] = useState(start.date);
  const [location, setLocation] = useState(start.location);
  const [branchId, setBranchId] = useState(start.branchId || "");
  const [category, setCategory] = useState(start.category);
  const [paidTo, setPaidTo] = useState(start.paidTo || "");
  const [amount, setAmount] = useState(start.amount);
  const [note, setNote] = useState(start.note || "");
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const save = () => {
    const fields = {
      date, location, branchId: location === "CABANG" ? (branchId || null) : null,
      category, paidTo, amount: typeof amount === "number" ? amount : null, note,
    };
    const res = onSave(fields);
    if (res && res.ok === false) { setError(res.error); return; }
    onClose();
  };

  return (
    <>
      <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
        <div className="modal-box" onMouseDown={(e) => e.stopPropagation()}>
          <h2 className="modal-title">{isEdit ? "Ubah Pengeluaran" : "Tambah Pengeluaran"}</h2>
          <div className="form-hint" style={{ marginBottom: "var(--gap-md)" }}>
            Biaya yang <b>kita</b> bayar sendiri dan tidak terkait transaksi tertentu — misalnya parkir. Biaya untuk sebuah
            transaksi dicatat di kotak <b>Biaya Lain-Lain</b> pada transaksi itu.
          </div>
          {orphanNote && <div className="alert alert--info" style={{ marginBottom: "var(--gap-md)" }} data-testid="expense-orphan-note">{orphanNote}</div>}
          <div className="form-row">
            <label className="form-label" htmlFor="expense-date">Tanggal</label>
            <input id="expense-date" type="date" className="form-input" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="form-row">
            <span className="form-label">Lokasi</span>
            <div className="seg">
              <button type="button" className={location === "PUSAT" ? "active" : ""} onClick={() => setLocation("PUSAT")}>Pusat</button>
              <button type="button" className={location === "CABANG" ? "active" : ""} onClick={() => setLocation("CABANG")}>Cabang</button>
            </div>
          </div>
          {location === "CABANG" && (
            <div className="form-row">
              <label className="form-label" htmlFor="expense-branch">Cabang</label>
              <select id="expense-branch" className="form-select" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">— Pilih cabang —</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          <div className="form-row">
            <label className="form-label" htmlFor="expense-category">Kategori</label>
            <select id="expense-category" className="form-select" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">— Pilih kategori —</option>
              {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              {category && !EXPENSE_CATEGORIES.includes(category) && <option value={category}>{category}</option>}
            </select>
          </div>
          <div className="form-row">
            <label className="form-label" htmlFor="expense-paidto">Dibayar ke</label>
            <input id="expense-paidto" className="form-input" placeholder="opsional, mis. tukang parkir" value={paidTo} onChange={(e) => setPaidTo(e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label" htmlFor="expense-amount">Jumlah</label>
            <div className="currency-input">
              <span className="currency-input__prefix">Rp</span>
              <input id="expense-amount" type="text" inputMode="numeric" value={formatCurrencyInput(amount)}
                onChange={(e) => setAmount(parseCurrencyInput(e.target.value))} />
            </div>
          </div>
          <div className="form-row">
            <label className="form-label" htmlFor="expense-note">Keterangan</label>
            <input id="expense-note" className="form-input" placeholder="opsional" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          {error && <div className="form-hint" style={{ color: "var(--red)" }} role="alert">{error}</div>}
          <div className="modal-actions">
            {isEdit && onDelete && (
              <button type="button" className="btn btn-danger" style={{ marginRight: "auto" }} onClick={() => setConfirmDelete(true)}>Hapus</button>
            )}
            <button type="button" className="btn" onClick={onClose}>Batal</button>
            <button type="button" className="btn btn-primary" onClick={save}>{isEdit ? "Simpan" : "Tambah"}</button>
          </div>
        </div>
      </div>
      {confirmDelete && (
        <DeleteConfirmModal
          title="Hapus pengeluaran?"
          message="Pengeluaran ini akan dihapus permanen."
          onConfirm={() => { onDelete(); setConfirmDelete(false); onClose(); }}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </>
  );
}
